import { __decorate } from "tslib";
import { html, nothing } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { property, queryAll, state } from 'lit/decorators.js';
import { emit } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/event.js';
import { FormRegistrationMixin } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/form/form-registration.js';
import { InternalI18n } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/i18n/internal-i18n-controller.js';
import { ValidityGroupMixin } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/form/validity-group.js';
import { renderItems } from './generate-elements.js';
import '@cigna/cigna_csg_gsg_cipublic-icon/cipublic-icon.js';
import styles from './checkgroup.scss.js';
let counter = 0;
/**
 * @param Base The base class.
 * @returns Checkgroup component.
 *
 * @event handleBlur - handles blur events.
 * @event handleChange - Handles change events.
 * @event submit - Fires when the form is submitted.
 * @event formdata - Add form data to the submit process.
 */
export class Checkgroup extends FormRegistrationMixin(ValidityGroupMixin(CipublicElement)) {
    constructor() {
        super(...arguments);
        this.i18n = new InternalI18n(this);
        /**
         * Indicates if the group should be disabled.
         *
         * **Note:** Will remove the element from `formdata` participation.
         */
        this.disabled = false;
        /**
         * Indicates if checkgroup values should be parsed. Should be used in
         * situations where we encode formdata using `URLSearchParams`. (Eloqua)
         */
        this.enableDataParsing = false;
        /**
         * Toggle element visibility.
         *
         * **Note:** Will remove the element from `formdata` participation.
         */
        this.hidden = false;
        /**
         * ID to associate the label with the element.
         */
        this._id = ++counter;
        /**
         * Constructor name.
         */
        this._name = 'checkgroup';
        /**
         * Optional Text.
         */
        this.optional = this.i18n.t('optional');
        /**
         * Indicate if the element is required. Defaults to `false`.
         */
        this.required = false;
        /**
         * Minimum number of required selected elements. Defaults to `1`.
         */
        this.selectedMin = 1;
        /**
         * The internal selected state.
         */
        this.value = [];
    }
    static get styles() {
        return [styles];
    }
    /**
     * Excludes the element from `formdata` participation if `true`.
     *
     * **Note:** This property is enabled by default for `disabled` / `hidden` elements.
     */
    get deregistered() {
        return this.disabled || this.hidden ? true : false;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('checkitem.blur', this.handleBlur);
        this.addEventListener('checkitem.change', this.handleChange);
    }
    firstUpdated(changedProperties) {
        var _a, _b;
        super.firstUpdated(changedProperties);
        (_a = this.queryItems[0]) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-invalid', 'false');
        /*
          There's a timing issue settings these default prop values.
          For cases where we are generating these elements via directives,
          it doesn't have enough time to query generated items (querySelectorAll).
    
          TODO - move logic to props
            this is inefficient - causes Lit to do another render
        */
        this.selectedMax = (_b = this.selectedMax) !== null && _b !== void 0 ? _b : (this.selectedMax = this.queryItems.length);
        this.value = [...this.queryItems].filter((item) => item.checked).map(item => item.value) || [];
    }
    updated(changedProperties) {
        var _a, _b;
        super.updated(changedProperties);
        if (changedProperties.has('disabled')) {
            if (this.disabled) {
                this.queryItems.forEach(checkitem => checkitem.checked = false);
                this.value = [];
            }
        }
        if (changedProperties.has('errorCustomMessage')) {
            if (this.errorCustomMessage) {
                this._showGroupError();
                this._showGroupSuccess();
            }
        }
        if (changedProperties.has('required')) {
            if (this.required) {
                (_a = this.queryItems[0]) === null || _a === void 0 ? void 0 : _a.setAttribute('required', '');
            }
            else {
                (_b = this.queryItems[0]) === null || _b === void 0 ? void 0 : _b.removeAttribute('required');
                this._showGroupSuccess();
            }
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('checkitem.blur', this.handleBlur);
        this.removeEventListener('checkitem.change', this.handleChange);
    }
    handleBlur() {
        this._showGroupError();
        this._showGroupSuccess();
    }
    handleChange(event) {
        const checkitem = event.detail.element;
        checkitem.checked
            ? this.value.push(checkitem.value)
            : this.value.splice(this.value.indexOf(checkitem.value), 1);
        this.requestUpdate();
        emit(this, 'checkgroup.change');
    }
    _handleFormSubmit(event) {
        var _a;
        const { queryItems } = this;
        const error = this._showGroupError();
        this._showGroupSuccess();
        if (error) {
            // halt form submission behavior
            event.preventDefault();
            if (this === this._getFirstErroredElement()) {
                (_a = queryItems[0]) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
        else {
            // dispatch 'FormData' event
            super._handleFormSubmit(event);
        }
    }
    render() {
        const { hint, label, optional, required } = this;
        const idName = `${this._name}-${this._id}`;
        return html `<fieldset aria-describedby="${idName}-hint ${idName}-error"><legend class="label">${label} ${!required ? optional : nothing}</legend><div class="items">${renderItems(this)}</div>${hint ? html `<span class="hint" id="${idName}-hint">${hint}</span>` : html `${nothing}`} <span aria-live="polite" class="error-container" id="${idName}-error" role="alert"><cipublic-icon set="system" name="exclamation-circle" class="error-icon"></cipublic-icon><p></p></span></fieldset>`;
    }
}
__decorate([
    state()
], Checkgroup.prototype, "deregistered", null);
__decorate([
    property({ reflect: true, type: Boolean })
], Checkgroup.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Checkgroup.prototype, "enableDataParsing", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Checkgroup.prototype, "hidden", void 0);
__decorate([
    property({ reflect: true })
], Checkgroup.prototype, "hint", void 0);
__decorate([
    property({ reflect: true })
], Checkgroup.prototype, "horizontal", void 0);
__decorate([
    property({ reflect: true })
], Checkgroup.prototype, "label", void 0);
__decorate([
    property({ reflect: true })
], Checkgroup.prototype, "name", void 0);
__decorate([
    property({ reflect: true })
], Checkgroup.prototype, "optional", void 0);
__decorate([
    queryAll('input')
], Checkgroup.prototype, "queryItems", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Checkgroup.prototype, "required", void 0);
__decorate([
    property({ reflect: true, type: Number, attribute: 'selected-max' })
], Checkgroup.prototype, "selectedMax", void 0);
__decorate([
    property({ reflect: true, type: Number, attribute: 'selected-min' })
], Checkgroup.prototype, "selectedMin", void 0);
__decorate([
    state()
], Checkgroup.prototype, "value", void 0);
//# sourceMappingURL=checkgroup.js.map